import React, { useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Cart } from "../../types";

type Props = {
  onApply: (count: number) => Promise<Cart>;
  onCartUpdate: (cart: Cart) => void;
  // Hard ceiling on the quantity. Generic — the domain decides what the
  // cap is (e.g. event registration passes min(attendees, available
  // voucher seats)); the selector just enforces it. Undefined = no cap.
  maxQuantity?: number;
};

export function VoucherSelector({ onApply, onCartUpdate, maxQuantity }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clamp to [0, maxQuantity] — blocks typing, pasting, or spinning the
  // count above what's actually available.
  const clampCount = (raw: number): number => {
    if (Number.isNaN(raw)) return 0;
    const lower = Math.max(0, raw);
    return maxQuantity !== undefined ? Math.min(lower, maxQuantity) : lower;
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await onApply(count);
      onCartUpdate(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply vouchers");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="voucher-selector">
      <button
        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium py-1"
        onClick={() => setExpanded((v) => !v)}
        type="button"
      >
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        Have a voucher?
      </button>

      {expanded ? (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              aria-label="Voucher count"
              className="w-20 h-9"
              disabled={busy}
              max={maxQuantity}
              min={0}
              onChange={(e) => setCount(clampCount(Number(e.target.value)))}
              type="number"
              value={count}
            />
            <Button
              aria-label="Apply vouchers"
              disabled={busy}
              onClick={apply}
              size="sm"
              type="button"
              variant="outline"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>
          {error ? (
            <p className="text-sm text-red-600" data-testid="voucher-error">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
